import { QueryCache, type VueQueryPluginOptions } from '@tanstack/vue-query';

import * as Sentry from '@sentry/vue';
import axios from 'axios';

import type { GetApiParcelFinderParams } from '@/composables/api/parcelFinder/types';
import type { GetParcelShopsBboxParams } from '@/composables/api/parcelShops/types';
import type {
  GetApiActivitiesParams,
  GetApiActivitiesRttParams,
  GetApiActivitiesSummaryParams,
  GetApiTourTuningRecommendationsParams
} from '@/composables/api/usageAnalysis/types';
import type {
  DriversPayload,
  GetApiV2UsersParams
} from '@/composables/api/users/types';

export const vueQueryOptions: VueQueryPluginOptions = {
  enableDevtoolsV6Plugin: true,
  queryClientConfig: {
    queryCache: new QueryCache({
      onError: (error, query) => {
        console.error(`[Global Query Error]: ${error.message}`);

        // Handle Axios-specific errors
        if (axios.isAxiosError(error)) {
          // 1. FILTER: Ignore cancellations (user navigating away)
          // This stops the "Network Error" on navigation from flooding Sentry
          if (axios.isCancel(error)) return;

          // 2. FILTER: Ignore status 0 (often related to navigation or lost connection)
          // If it's a persistent issue, Sentry will likely catch it elsewhere
          if (axios.isAxiosError(error) && error.response?.status === 0) return;

          // 3. Capture everything else to Sentry
          Sentry.captureException(error, {
            extra: {
              queryKey: query.queryKey,
              // Include helpful metadata for debugging
              url: error.config?.url,
              method: error.config?.method,
              status: error.response?.status,
              isOnline: navigator.onLine
            },
            // Tag it so you can filter in the Sentry Dashboard
            tags: {
              category: 'api-query',
              layer: 'tanstack-query'
            }
          });
        }

        // Handle non-Axios errors (General JS crashes)
        Sentry.captureException(error, {
          extra: { queryKey: query.queryKey }
        });

        // @TODO: Trigger a UI notification for 500s
        // Example: Global snackbar notification
        if (
          axios.isAxiosError(error) &&
          error.response &&
          error.response.status >= 500
        ) {
          // triggerGlobalSnackbar(t('errors.server_error'));
        }
      }
    }),
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: 2,
        // Using a clear variable name for the 2-minute stale time
        staleTime: 1000 * 60 * 2,
        // Ensures data stays in cache for 5 mins even if not being used
        gcTime: 1000 * 60 * 5
      }
    }
  }
};

type WidgetScope = 'depot' | 'suco';

/** * Centralized Query Keys
 * Prevents typos like ['widget', 'todo'] vs ['widgets', 'todos']
 */
export const queryKeys = {
  liveStatus: (internalDepotId: number, subcontractorId?: string) =>
    ['liveStatus', internalDepotId, subcontractorId] as const,

  users: {
    all: ['users'] as const,
    // Base key for all user lists
    lists: () => [...queryKeys.users.all, 'list'] as const,
    // Granular key for a specific search/filter/page combination
    filteredLists: (payload: GetApiV2UsersParams) =>
      [...queryKeys.users.lists(), payload] as const
  },

  // App-account drivers list (GET /v3/users/app). Kept in its own namespace,
  // separate from `users`, so driver-list cache ops never cross-invalidate the
  // BO users list (they hit different endpoints).
  drivers: {
    all: ['drivers'] as const,
    lists: () => [...queryKeys.drivers.all, 'list'] as const,
    filteredLists: (payload: DriversPayload) =>
      [...queryKeys.drivers.lists(), payload] as const
  },

  subcontractors: {
    all: ['subcontractors'] as const,
    lists: () => [...queryKeys.subcontractors.all, 'list'] as const,
    // Scoped per distribution center; `undefined` while no depot is selected
    // (the query stays disabled for that key).
    list: (dcId: number | undefined) =>
      [...queryKeys.subcontractors.lists(), dcId] as const
  },

  parcelFinder: (payload: GetApiParcelFinderParams) =>
    ['parcelFinder', payload] as const,

  parcelShops: {
    all: ['parcelShops'] as const,
    bbox: (params: GetParcelShopsBboxParams | null) =>
      [...queryKeys.parcelShops.all, 'bbox', params] as const
  },

  widgets: {
    all: ['widgets'] as const,
    // Scope discriminator prevents cache collisions between a depot id and a
    // subcontractor id that happen to share the same value
    todos: (scope: WidgetScope, id: string) =>
      [...queryKeys.widgets.all, 'todos', scope, id] as const,
    rtt: (scope: WidgetScope, id: string) =>
      [...queryKeys.widgets.all, 'rtt', scope, id] as const,
    weather: (internalDepotId: string, days: number) =>
      [...queryKeys.widgets.all, 'weather', internalDepotId, days] as const
  },

  usageAnalysis: {
    all: ['usageAnalysis'] as const,
    bars: (payload: GetApiActivitiesSummaryParams) =>
      [...queryKeys.usageAnalysis.all, 'bars', payload] as const,
    activities: (payload: GetApiActivitiesParams) =>
      [...queryKeys.usageAnalysis.all, 'activities', payload] as const,
    rtt: (payload: GetApiActivitiesRttParams) =>
      [...queryKeys.usageAnalysis.all, 'rtt', payload] as const,
    tourRecommendations: (payload: GetApiTourTuningRecommendationsParams) =>
      [...queryKeys.usageAnalysis.all, 'tourRecommendations', payload] as const
  },

  // Payload is null while the depot scope is cleared (the query is disabled
  // for that key).
  tourPerformance: (
    payload: { dcId: number; from: string; to: string } | null
  ) => ['tourPerformance', payload] as const,

  tourDrivenHistory: {
    all: ['tourDrivenHistory'] as const,
    details: (tourId: string) =>
      [...queryKeys.tourDrivenHistory.all, 'details', tourId] as const,
    performance: (tourId: string) =>
      [...queryKeys.tourDrivenHistory.all, 'performance', tourId] as const,
    activities: (tourId: string) =>
      [...queryKeys.tourDrivenHistory.all, 'activities', tourId] as const,
    sequences: (tourId: string) =>
      [...queryKeys.tourDrivenHistory.all, 'sequences', tourId] as const,
    sequence: (tourId: string, sequenceId: number) =>
      [
        ...queryKeys.tourDrivenHistory.all,
        'sequence',
        tourId,
        sequenceId
      ] as const,
    history: (tourId: string) =>
      [...queryKeys.tourDrivenHistory.all, 'history', tourId] as const
  }
};
